import * as React from 'react';
import { observer } from 'mobx-react-lite';
import loc, { format } from 'owa-localize';
import {
    durationDay,
    durationFirstPluralDays,
    durationLabel,
} from './DurationDropdown.locstring.json';
import { custom } from 'owa-locstrings/lib/strings/custom.locstring.json';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import generateDefaultDropdownStyles from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDefaultDropdownStyles';
import { Dropdown, IDropdownOption, DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';

export interface DurationDropdownProps {
    /**
     * Callback to execute when duration is selected from dropdown
     */
    onDurationChanged: (duration: number) => void;

    /**
     * Callback to execute when custom is selected from dropdown
     */
    onCustomSelected: () => void;

    /**
     * Selected duration
     */
    selectedDurationInDays: number;

    /**
     * True to hide the default border
     */
    hideBorder?: boolean;

    colorScheme?: ColorScheme;
    className?: string;
    disabled?: boolean;
}

/**
 * Duration between days will be one less since we consider a single day, all day event
 * to have one day duration even though the start and end dates appear on the same day on selection.
 * The user wants a 1 day event from start:Aug 11 end: Aug 11, the event will be saved as start: Aug 11, 12am end Aug 12, 12am
 */
export enum DurationDayLength {
    One = 0,
    Two,
    Three,
    Four,
    Five,
    Custom,
}

const DurationDropdown: React.FunctionComponent<DurationDropdownProps> = props => {
    const {
        onDurationChanged,
        onCustomSelected,
        selectedDurationInDays,
        colorScheme,
        className,
        disabled,
    } = props;

    const selectedKey =
        selectedDurationInDays >= 0 && selectedDurationInDays <= 5
            ? (selectedDurationInDays as DurationDayLength)
            : DurationDayLength.Custom;

    const durationOptions: IDropdownOption[] = [
        {
            key: DurationDayLength.One,
            text: format(loc(durationDay), 1),
        },
        {
            key: DurationDayLength.Two,
            text: format(loc(durationFirstPluralDays), 2),
        },
        {
            key: DurationDayLength.Three,
            text: format(loc(durationFirstPluralDays), 3),
        },
        {
            key: DurationDayLength.Four,
            text: format(loc(durationFirstPluralDays), 4),
        },
        {
            key: DurationDayLength.Five,
            text: format(loc(durationFirstPluralDays), 5),
        },
        {
            key: 'duration_divider',
            text: '-',
            itemType: DropdownMenuItemType.Divider,
        },
        {
            key: DurationDayLength.Custom,
            text: loc(custom),
        },
    ];

    /**
     * Callback issued when the menu item is invoked
     */
    const onChange = React.useCallback(
        (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            const selectedOption = option.key as DurationDayLength;
            if (selectedOption == DurationDayLength.Custom) {
                onCustomSelected();
            } else {
                onDurationChanged(selectedOption);
            }
        },
        [onDurationChanged, onCustomSelected]
    );

    const styleOverrides = {
        root: { position: 'relative', height: '32px', boxSizing: 'border-box', padding: '0' },
        dropdown: { display: 'flex', width: '100%', marginTop: '2px', marginLeft: '1px' },
        title: { paddingLeft: '10px' },
        caretDownWrapper: { right: '12px' },
    };

    return (
        <Dropdown
            calloutProps={{
                gapSpace: 5,
            }}
            ariaLabel={loc(durationLabel)}
            className={className}
            onChange={onChange}
            options={durationOptions}
            selectedKey={selectedKey}
            styles={generateDefaultDropdownStyles(colorScheme, styleOverrides)}
            disabled={disabled}
        />
    );
};

export default observer(DurationDropdown);
